// [목적] 알림 도메인 REST API 클라이언트 — /users/me/notifications 엔드포인트 매핑
// [이유] fe-be-gap-tier3 T8(ADR-TR3-8) — NotificationsPage 실데이터 연결을 위한
//         getNotifications / markRead / markAllRead 단일 모듈 캡슐화.
//         @gangwoncanvas/types 의 notificationListResponseSchema 로 zod 파싱(§6-5).
// [사이드 이팩트] fetchWithEnvelope 경유 — CSRF 헤더(POST) / 인증 쿠키 자동 첨부.
//                 getNotifications 응답에 unreadCount 포함(전체 미읽 카운트, 현재 페이지 한정 아님).
// [수정 시 고려사항] tab/cursor/limit 파라미터는 notificationListQuerySchema 와 동기 유지.
//                   markRead / markAllRead 는 JwtAuthGuard + CsrfGuard — 비로그인 401.
//                   엔드포인트 prefix 'users/me/notifications' 는 BE NotificationsController 경로와 동기.

import {
  notificationListResponseSchema,
  notificationReadAllResponseSchema,
  type NotificationTab,
  type NotificationListResponse,
  type NotificationReadAllResponse,
} from '@gangwoncanvas/types';
import { fetchWithEnvelope } from './http.js';

// ─── 내부 헬퍼 ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ─── API 메서드 ────────────────────────────────────────────────────────────────

/**
 * [목적] GET /users/me/notifications — 본인 알림 목록 (탭 필터 + 키셋 커서 페이지네이션)
 * [이유] fe-be-gap-tier3 T8 — NotificationsPage 가 useInfiniteQuery 로 탭별 목록 조회.
 *         tab: all(전체)/social(like|fork|follow|comment)/system(system|badge) — ADR-T3-2.
 *         unreadCount 는 전체 미읽 카운트(현재 페이지 아님).
 * [사이드 이팩트] JwtAuthGuard 필수 — 비로그인 401.
 * [수정 시 고려사항] limit 기본값은 BE(20)와 동일. cursor 미전달 시 첫 페이지.
 */
export async function getNotifications(opts: {
  tab?: NotificationTab;
  cursor?: string;
  limit?: number;
}): Promise<NotificationListResponse> {
  const q = buildQuery({
    tab: opts.tab,
    cursor: opts.cursor,
    limit: opts.limit,
  });
  const data = await fetchWithEnvelope<unknown>(`/users/me/notifications${q}`);
  return notificationListResponseSchema.parse(data);
}

/**
 * [목적] POST /users/me/notifications/:id/read — 단건 알림 읽음 처리
 * [이유] fe-be-gap-tier3 T8 — 알림 클릭 시 개별 읽음 mutation.
 *         BE: read_at = now()(이미 읽음이면 idempotent). 타인 소유/부재 id 는 404.
 * [사이드 이팩트] CsrfGuard 적용 — http.ts 가 CSRF 헤더 자동 첨부.
 * [수정 시 고려사항] 204 응답 시 undefined 반환(http.ts 처리) — void 로 사용.
 */
export async function markRead(id: string): Promise<void> {
  await fetchWithEnvelope<unknown>(`/users/me/notifications/${id}/read`, {
    method: 'POST',
  });
}

/**
 * [목적] POST /users/me/notifications/read-all — 본인 미읽 알림 전체 읽음 처리
 * [이유] fe-be-gap-tier3 T8 — "모두 읽음" 버튼 mutation.
 *         BE: UPDATE ... SET read_at=now() WHERE user_id=me AND read_at IS NULL.
 * [사이드 이팩트] CsrfGuard 적용. 처리 건수(affected) 반환.
 * [수정 시 고려사항] read-all 은 정적 세그먼트 — BE 라우터 우선순위 주의(ADR-TR3-2).
 */
export async function markAllRead(): Promise<NotificationReadAllResponse> {
  const data = await fetchWithEnvelope<unknown>('/users/me/notifications/read-all', {
    method: 'POST',
  });
  return notificationReadAllResponseSchema.parse(data);
}
